package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceLineResponse
import ai.jessie.shared.invoices.InvoiceTotalsResponse
import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CalendarScrollStyle
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardHeadStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.FieldErrorStyle
import ai.jessie.web.theme.HeadTableRowVariant
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.InvoiceLineTableRowStyle
import ai.jessie.web.theme.InvoiceLineTableStyle
import ai.jessie.web.theme.InvoiceTotalsStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.PositionKindStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.SubListStyle
import ai.jessie.web.theme.TableNumHeadStyle
import ai.jessie.web.theme.TableNumStyle
import ai.jessie.web.theme.TableRowKind
import ai.jessie.web.theme.TableRowStyle
import ai.jessie.web.theme.TableStrongStyle
import ai.jessie.web.theme.TableStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// Steps 8-9: the positions and totals exactly as the server calculated them. A position that the
// server rejected is listed with its corrections so that the member removes or replaces it (A6).
// The next position type is chosen in the same card.
@Composable
internal fun invoiceReview(state: InvoiceUiState, actions: InvoiceActions) {
    val draft = state.draft ?: return
    val preview = state.preview
    invoicePositions(draft.currency, "invoice-lines") {
        if (draft.lines.isEmpty() || preview == null)
            Div(attrs = CardBodyStyle.toAttrs()) {
                if (draft.lines.isEmpty())
                    P(attrs = HintStyle.toAttrs()) {
                        Text("Noch keine Positionen.")
                    }
                else rejectedLines(state, actions)
            }
        else
            invoiceLines(
                preview.lines,
                preview.currency,
                state.problems,
                state.busy,
                actions.removeLine,
            )
        preview?.let { invoiceTotals(it.totals, it.currency, state.problems) }
        Div(attrs = CardBodyStyle.toAttrs()) {
            if (state.line == null) lineTypeChoice(state, actions)
            if (draft.lines.isNotEmpty())
                Div(attrs = RowStyle.toAttrs()) {
                    themedButton(
                        "Positionen neu berechnen",
                        actions.recalculate,
                        size = ButtonSize.SMALL,
                        disabled = state.busy,
                    )
                }
        }
    }
}

// A6: a rejected position has no calculated values; it is named so that it can be removed.
@Composable
private fun rejectedLines(state: InvoiceUiState, actions: InvoiceActions) {
    val draft = state.draft ?: return
    P(attrs = HintStyle.toAttrs()) {
        Text("Die Positionen wurden noch nicht berechnet. Es werden keine Summen angezeigt.")
    }
    Ol(attrs = StackStyle.toAttrs { style { property("margin", "0") } }) {
        draft.lines.forEachIndexed { index, line ->
            Li(attrs = { id("invoice-line-$index") }) {
                Text(lineTypeLabel(line.type))
                lineProblems(index, state.problems)
                removeButton(index, state.busy, actions.removeLine)
            }
        }
    }
}

/** The positions card (`06-Rechnung.png`): head, then the caller's table and totals. */
@Composable
internal fun invoicePositions(
    currency: String,
    sectionId: String? = null,
    content: @Composable () -> Unit,
) {
    Section(attrs = CardStyle.toAttrs { sectionId?.let { id(it) } }) {
        Div(attrs = CardHeadStyle.toAttrs()) {
            H2 { Text("Positionen") }
            if (currency.isNotBlank()) P { Text("Beträge in $currency") }
        }
        content()
    }
}

@Composable
internal fun invoiceLines(
    lines: List<InvoiceLineResponse>,
    currency: String,
    problems: Map<String, String> = emptyMap(),
    busy: Boolean = false,
    remove: ((Int) -> Unit)? = null,
) {
    if (lines.isEmpty()) {
        Div(attrs = CardBodyStyle.toAttrs()) {
            P(attrs = HintStyle.toAttrs()) { Text("Keine Positionen.") }
        }
        return
    }
    Div(attrs = CalendarScrollStyle.toAttrs()) {
        Div(
            attrs =
                TableStyle.toModifier().then(InvoiceLineTableStyle.toModifier()).toAttrs {
                    attr("role", "table")
                    attr("aria-label", "Positionen")
                }
        ) {
            lineRow(HeadTableRowVariant, null) {
                header("Position")
                listOf("Menge", "Preis").forEach { header(it, numeric = true) }
                header("MwSt.-Satz")
                listOf("Netto", "MwSt.", "Brutto").forEach { header(it, numeric = true) }
                header("")
            }
            lines.forEachIndexed { index, line ->
                lineRow(null, "invoice-line-$index") {
                    Div(attrs = { attr("role", "cell") }) {
                        Div(attrs = PositionKindStyle.toAttrs()) {
                            Span { Text(lineTypeLabel(line.type)) }
                        }
                        Div(attrs = TableStrongStyle.toAttrs()) { Text(line.description) }
                        Div(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
                            Text(quantityLabel(line, currency))
                        }
                        if (line.work.isNotEmpty())
                            Ul(attrs = listOf(SubListStyle, SmallStyle, MutedStyle).toAttrs()) {
                                line.work.forEach { work ->
                                    Li {
                                        Text(
                                            "Arbeit vom ${work.workDate}: ${work.billedMinutes} Min."
                                        )
                                    }
                                }
                            }
                        lineProblems(index, problems)
                    }
                    numCell(line.quantity)
                    numCell(groupedAmount(line.unitPrice))
                    Span(attrs = { attr("role", "cell") }) {
                        Text(vatLabel(line.vatName, line.vatRate))
                    }
                    numCell(groupedAmount(line.totalNet))
                    numCell(groupedAmount(line.totalVat))
                    numCell(groupedAmount(line.totalGross))
                    Span(attrs = { attr("role", "cell") }) {
                        remove?.let { removeButton(index, busy, it) }
                    }
                }
            }
        }
    }
}

@Composable
private fun header(label: String, numeric: Boolean = false) {
    if (numeric)
        Span(attrs = TableNumHeadStyle.toAttrs { attr("role", "columnheader") }) { Text(label) }
    else Span(attrs = { attr("role", "columnheader") }) { Text(label) }
}

@Composable
private fun numCell(value: String) {
    Span(attrs = TableNumStyle.toAttrs { attr("role", "cell") }) { Text(value) }
}

@Composable
private fun lineRow(
    variant: CssStyleVariant<TableRowKind>?,
    rowId: String?,
    cells: @Composable () -> Unit,
) {
    Div(
        attrs =
            TableRowStyle.toModifier(variant).then(InvoiceLineTableRowStyle.toModifier()).toAttrs {
                rowId?.let { id(it) }
                attr("role", "row")
            }
    ) {
        cells()
    }
}

@Composable
private fun removeButton(index: Int, busy: Boolean, remove: (Int) -> Unit) {
    themedButton(
        "Position entfernen",
        { remove(index) },
        variant = ButtonVariant.GHOST,
        size = ButtonSize.SMALL,
        disabled = busy,
    )
}

@Composable
private fun lineProblems(index: Int, problems: Map<String, String>) {
    val prefix = "lines[$index]"
    val own = problems.filterKeys { it == prefix || it.startsWith("$prefix.") }
    if (own.isEmpty()) return
    Ul(attrs = listOf(SubListStyle, FieldErrorStyle).toAttrs()) {
        own.forEach { (field, correction) ->
            Li { Text("${invoiceFieldLabel(field)}: $correction") }
        }
    }
}

// BR-075: totals are the server's sums of the rounded positions; nothing is added up here.
@Composable
internal fun invoiceTotals(
    totals: InvoiceTotalsResponse,
    currency: String,
    problems: Map<String, String> = emptyMap(),
) {
    Div(attrs = { id("invoice-totals") }) {
        TagElement<HTMLElement>("dl", InvoiceTotalsStyle.toAttrs()) {
            TagElement<HTMLElement>("dt", null) { Text("Summe netto") }
            TagElement<HTMLElement>("dd", null) { Text(money(currency, totals.net)) }
            TagElement<HTMLElement>("dt", null) { Text("MwSt.") }
            TagElement<HTMLElement>("dd", null) { Text(money(currency, totals.vat)) }
            TagElement<HTMLElement>("dt", null) { Text("Brutto") }
            TagElement<HTMLElement>("dd", null) { Text(money(currency, totals.gross)) }
        }
        (problems["totals"] ?: problems["reviewedTotals"])?.let {
            P(attrs = FieldErrorStyle.toAttrs { style { property("padding", "0 20px 16px") } }) {
                Text(it)
            }
        }
    }
}
